/**
 * 設定 → ユーザー管理 → ユーザー追加のシート（040、docs/29 §5 → #156、docs/65 §5・Figma「設定の残り（10/9）」⑳。前は別の画面）。管理者だけ
 *   ・一覧の画面（SettingsUsers.tsx）が /settings/users/new で出す。スマホ版＝下から、PC版＝真ん中の枠
 *   ・#287（2026-11-01 射場さん ★1 A・★3、Figma「文言と画面の見直し（11/1）」③）：頭「取消｜ユーザー追加｜利用人数 2／3人」（上限は朱で「（上限）」）、
 *     題の下に1文「追加する利用者の氏名とメールアドレスを入力してください。」、主ボタン「追加」（下の 48px。id は今の sendInvite）。
 *     上限のときは押せない（注を朱で1行。API は 409 PLAN_SEATS_FULL）
 *   ・節「氏名」（姓・名）／メールアドレス（注「このアドレスにパスワード設定の案内メールを送信します」）／節「権限」（セグメント 担当者｜管理者、既定は担当者。注1行）
 *   ・間違いは欄の行の下に朱の1行（形・もういる）。入れかけのまま取消・Esc・後ろを押したら「入れた内容は消えます」の確かめ
 */
import { InviteRequest, REST_MSG, ROLE_LABEL, type UserRole, type UsersPage as UsersPageData } from "@genbaryoku/contract";
import { zodResolver } from "@hookform/resolvers/zod";
import { useMutation } from "@tanstack/react-query";
import { useId, useState } from "react";
import { useForm } from "react-hook-form";
import type { z } from "zod";
import { api, ApiError, messageOf } from "../api.js";
import { useMe } from "../auth.js";
import { LeaveConfirm } from "../components/BackButton.jsx";
import { GroupInputRow, GroupList, GroupSection, GroupValueRow } from "../components/GroupList.jsx";
import { Segmented } from "../components/Segmented.jsx";
import { Sheet } from "../components/Sheet.jsx";
import { ErrorText, Help, guardComposingEnter, useFieldIds } from "../components/ui.jsx";
import { useProduct } from "../lib/useProduct.js";
import { limitText } from "./SettingsUsers.jsx";

export function UserInviteSheet({ open, seats, onClose, onSaved }: { open: boolean; seats: UsersPageData["seats"]; onClose: () => void; onSaved: (p: UsersPageData & { message: string }) => void }) {
  const me = useMe();
  const P = useProduct(me.data);
  const ids = useFieldIds(["family", "given", "email"] as const);
  const roleId = useId();
  const [serverError, setServerError] = useState<string | undefined>();
  const [askLeave, setAskLeave] = useState(false);
  const form = useForm<z.input<typeof InviteRequest>, unknown, InviteRequest>({
    resolver: zodResolver(InviteRequest),
    defaultValues: { familyName: "", givenName: "", email: "", role: "member" },
    shouldFocusError: true,
  });
  const role = (form.watch("role") ?? "member") as UserRole;
  const send = useMutation({
    mutationFn: (v: InviteRequest) => api<UsersPageData & { message: string }>("POST", "/users/invite", v),
    onSuccess: onSaved,
    onError: (e) => {
      if (e instanceof ApiError && e.problem.errors) {
        for (const [k, v] of Object.entries(e.problem.errors)) form.setError(k as "email" | "familyName", { message: String(v) });
        return;
      }
      setServerError(messageOf(e));
    },
  });
  const full = seats.full;
  const e = form.formState.errors;
  const dirty = form.formState.isDirty;
  const cancel = () => {
    if (dirty && !send.isPending) setAskLeave(true);
    else onClose();
  };
  const inv = (msg: string | undefined, id: string) => (msg ? { "aria-invalid": true as const, "aria-describedby": `${id}-err` } : {});

  return (
    <>
      <Sheet
        open={open}
        title={REST_MSG.inviteTitle}
        cancelLabel="取消"
        doneLabel={send.isPending ? REST_MSG.sending : REST_MSG.send}
        doneDisabled={full || send.isPending}
        onCancel={cancel}
        onDone={() =>
          void form.handleSubmit((v) => {
            setServerError(undefined);
            send.mutate(v);
          })()
        }
        className="usersheet"
        lead={REST_MSG.inviteLead}
        headEnd={
          <span className={"chip usersheet-seats" + (full ? " is-full" : "")} data-testid="invite-seats">
            {REST_MSG.seatsChip.replace("{used}", String(seats.used)).replace("{limit}", String(seats.limit))}
            {full ? <span className="shu">{REST_MSG.seatsChipFull}</span> : null}
          </span>
        }
        bottomDone
        doneId="sendInvite"
        testId="invite-sheet"
        formTestId="invForm"
        cancelTestId="invite-cancel"
      >
        {full ? (
          <p className="banksheet-note shu" id="inviteLimitNote" role="status">
            {limitText(seats)}
          </p>
        ) : null}
        <ErrorText>{serverError}</ErrorText>
        <GroupSection title={REST_MSG.sectionName} data-testid="invite-sec-name">
          <GroupList plain>
            <GroupInputRow id={ids.family} label="姓" wide error={e.familyName?.message}>
              <input id={ids.family} className="giinp" autoComplete="off" maxLength={50} {...inv(e.familyName?.message, ids.family)} onKeyDown={guardComposingEnter} {...form.register("familyName")} />
            </GroupInputRow>
            <GroupInputRow id={ids.given} label="名" wide error={e.givenName?.message}>
              <input id={ids.given} className="giinp" autoComplete="off" maxLength={50} {...inv(e.givenName?.message, ids.given)} onKeyDown={guardComposingEnter} {...form.register("givenName")} />
            </GroupInputRow>
          </GroupList>
        </GroupSection>
        <GroupSection note={REST_MSG.emailNote} data-testid="invite-sec-email">
          <GroupList plain>
            <GroupInputRow id={ids.email} label={REST_MSG.email} wide error={e.email?.message} end={<Help testId="help-invite-email" text={REST_MSG.emailHelp.replace("{product}", P.name)} />}>
              <input id={ids.email} className="giinp" type="email" inputMode="email" autoComplete="off" {...inv(e.email?.message, ids.email)} onKeyDown={guardComposingEnter} {...form.register("email")} />
            </GroupInputRow>
          </GroupList>
        </GroupSection>
        <GroupSection title={REST_MSG.sectionRole} help={<Help testId="help-invite-role" text={REST_MSG.roleHelp} />} note={REST_MSG.roleNote} data-testid="invite-sec-role">
          <GroupList plain>
            <GroupValueRow
              title={REST_MSG.role}
              titleId={roleId}
              className="grow-seg is-in"
              value={
                <Segmented
                  id="roleSeg"
                  options={(["member", "admin"] as UserRole[]).map((r) => ({ value: r, label: ROLE_LABEL[r], testId: `role-${r}` }))}
                  value={role}
                  onChange={(r) => form.setValue("role", r, { shouldDirty: true })}
                  labelledBy={roleId}
                />
              }
            />
          </GroupList>
        </GroupSection>
      </Sheet>
      {askLeave ? (
        <LeaveConfirm
          onStay={() => setAskLeave(false)}
          onLeave={() => {
            setAskLeave(false);
            onClose();
          }}
        />
      ) : null}
    </>
  );
}
